/**
 * Counterparty Ledger — read-only tab (2026-07-09, Task 22 of the
 * counterparty-ledger plan: the /api/cp-ledger API's first UI consumer).
 *
 * Shows every counterparty with a non-zero AP outstanding or AR balance
 * (GBP functional), grouped by type, with a per-counterparty statement
 * drill-down. Data is rebuilt nightly at 05:50 UTC (cp_ledger_refresh
 * pipeline) and reconciled to the GL at 06:20 (cp_ledger_recon).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  TextField,
  MenuItem
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getCpLedgerSummary, getCpLedgerStatement } from '../../../services/cpLedgerService';

const TYPE_LABELS = {
  supplier: 'Supplier',
  gateway: 'Gateway',
  marketplace: 'Marketplace',
  ad_platform: 'Ad Platform',
  wholesale_customer: 'Wholesale Customer',
  tax_authority: 'Tax Authority',
  intercompany_entity: 'Intercompany'
};

const gbp = (v) =>
  Number(v || 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

function StatementRow({ row }) {
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !statement && !loading) {
      setLoading(true);
      try {
        const res = await getCpLedgerStatement(row.counterparty_key);
        setStatement(res.data || res);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const lines = statement?.lines || [];

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={toggle}>
        <TableCell padding="checkbox">
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.counterparty_key}</Typography>
        </TableCell>
        <TableCell>
          <Chip size="small" label={TYPE_LABELS[row.type] || row.type} />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={Number(row.ap_outstanding_gbp) > 0 ? 'error.main' : 'text.primary'}>
            {gbp(row.ap_outstanding_gbp)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={Number(row.ar_balance_gbp) > 0 ? 'success.main' : 'text.primary'}>
            {gbp(row.ar_balance_gbp)}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, border: open ? undefined : 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              {loading && <CircularProgress size={20} sx={{ m: 1 }} />}
              {error && <Alert severity="error">{error}</Alert>}
              {!loading && !error && lines.length === 0 && statement && (
                <Typography variant="body2" color="text.secondary" sx={{ m: 1 }}>
                  No open items.
                </Typography>
              )}
              {lines.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>AP/AR</TableCell>
                      <TableCell>Kind</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell align="right">Amount (GBP)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.slice(0, 50).map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>{l.txn_date}</TableCell>
                        <TableCell>{l.ap_ar}</TableCell>
                        <TableCell>{l.entry_kind}</TableCell>
                        <TableCell>
                          <Typography variant="caption">{l.source_type}: {l.source_id}</Typography>
                        </TableCell>
                        <TableCell align="right">{gbp(l.amount_gbp)}</TableCell>
                      </TableRow>
                    ))}
                    {lines.length > 50 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="caption" color="text.secondary">
                            … {lines.length - 50} more lines (showing latest 50)
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function CounterpartyLedger() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCpLedgerSummary();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const types = ['all', ...new Set(rows.map(r => r.type))];
  const visible = rows.filter(r =>
    (typeFilter === 'all' || r.type === typeFilter) &&
    (!search || r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.counterparty_key.toLowerCase().includes(search.toLowerCase()))
  );
  const totalAp = visible.reduce((s, r) => s + Number(r.ap_outstanding_gbp || 0), 0);
  const totalAr = visible.reduce((s, r) => s + Number(r.ar_balance_gbp || 0), 0);

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6">Counterparty Ledger</Typography>
          <Typography variant="body2" color="text.secondary">
            Who we owe and who owes us, per counterparty (GBP functional). Rebuilt nightly 05:50 UTC; reconciled to the GL 06:20.
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TextField
            size="small"
            select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {types.map(t => (
              <MenuItem key={t} value={t}>{t === 'all' ? 'All types' : (TYPE_LABELS[t] || t)}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}

      {!loading && !error && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Counterparty</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">AP Outstanding</TableCell>
                <TableCell align="right">AR Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => (
                <StatementRow key={row.counterparty_key} row={row} />
              ))}
              <TableRow>
                <TableCell />
                <TableCell><Typography variant="body2" fontWeight={700}>Total ({visible.length})</Typography></TableCell>
                <TableCell />
                <TableCell align="right"><Typography variant="body2" fontWeight={700}>{gbp(totalAp)}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700}>{gbp(totalAr)}</Typography></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
